import React, { Component, ChangeEvent } from 'react';
import moment, { Moment } from 'moment';
import { Typography, Radio, Tooltip, Label, Grid, Button, Option } from '@inplayer-org/inplayer-ui';
import styled from 'styled-components';
import { MdInfoOutline } from 'react-icons/md';

// Images
import packagePreviewImg from 'assets/pics/package_def.jpg';

// Types
import { LoadAssetsParams, ItemType } from 'ducks/merchantDucks/items/actions';
import { LoadPackagePayload } from 'ducks/merchantDucks/packages/actions';
import { ModalNames } from 'ducks/merchantDucks/modal/types';
import { AuthState } from 'ducks/merchantDucks/user/types';

// Components
import SingleDayPicker from 'components/SingleDayPicker';
import TimeHourPicker from 'components/TimeHourPicker';
import BaseModal, { ModalActions } from 'components/Modals/BaseModal';
import { ErrorMessage } from 'components/ErrorMessage';
import StyledContainer from 'components/StyledContainer';
import ThumbnailItemPicker, { Item } from 'components/ThumbnailItemPicker';
import { Optional } from 'utils/types';
import Filter from 'components/Filter';
import { transformSearchParams } from 'utils/helpers';
import some from 'lodash/some';
import SearchTooltip from 'components/SearchTooltip';
import GrantAccessPeriod from '../GrantAccessPeriod';
import { ButtonContainer, PeriodsWrapper, UtcWrapper, AccessPeriodContainer } from '../modals';

// utils
import { Operations, periods, Period } from '../../utils/datePeriodSelection';

const { Cell } = Grid;

export const DivWithMargin = styled.div`
  width: 100%;
  margin: 0.5rem 0;
`;

export const RadioButtonsContainer = styled(StyledContainer)`
  label {
    margin-right: 1rem;
  }
`;

export const VerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin: 1rem 0 0 0;
  min-height: 4.5rem;
`;

export interface AssetItem {
  id: string;
  title: string;
  metadata?: {
    paywall_cover_photo: string;
  };
}

export interface PackageItem {
  id: string;
  title: string;
  metadata?: {
    thumbnail_image: string;
  };
}

export const defaultAssetItemImg = 'https://assets.inplayer.com/images/preview-premium.jpg';

interface Props {
  assets: {
    collection: Array<any>;
    isFetching: boolean;
    total: number;
  };
  packages: {
    collection: Array<any>;
    isFetching: boolean;
    total: number;
  };
  customerEmail: string;
  customer: {
    id: number;
  } | null;
  loadPackages: (data: LoadPackagePayload) => any;
  loadAssets: (params: LoadAssetsParams) => any;
  auth: AuthState;
  resetPackageAssets: () => any;
  grantAccess: (consumerId: number, expiresAt: string, itemId: number) => any;
}

interface State {
  value: string | null;
  endDate: Moment;
  time: Moment;
  selectedItemId: number;
  activePeriod: string | null;
  tags: Array<string>;
  currentPage: number;
}

const suggestions: Array<Option> = [
  { value: 'id', displayName: 'ID' },
  { value: 'title', displayName: 'Name' },
];

interface SearchParams {
  id: Optional<number>;
  title: string;
}

const dateFormat = 'YYYY-M-D';
const hourFormat = 'HH:mm:ss';

export default class GrantAccessModal extends Component<Props, State> {
  state: State = {
    value: null,
    endDate: moment(),
    time: moment(),
    selectedItemId: 0,
    activePeriod: '',
    tags: [],
    currentPage: 1,
  };

  handleAddition = (searchParams: SearchParams) => {
    const { value } = this.state;
    const { resetPackageAssets } = this.props;

    const transformedParams = transformSearchParams(searchParams);

    if (some(transformedParams, Boolean)) {
      if (value === ItemType.Asset) {
        this.searchAsset(transformedParams);
      } else {
        this.searchPackage(transformedParams);
      }
    } else {
      resetPackageAssets();
    }

    this.setState({ tags: transformedParams });
  };

  handlePageSelect = (page: number, limit: number) => {
    const { tags, value } = this.state;

    if (value === ItemType.Asset) {
      this.searchAsset(tags, page, limit);
    } else {
      this.searchPackage(tags, page, limit);
    }
  };

  onItemSelect = ({ id }: Item) => {
    this.setState({ selectedItemId: parseInt(id) });
  };

  searchPackage = (tags: Array<string>, page = 1, limit = 9) => {
    const {
      loadPackages,
      auth: {
        user: { tenant_uuid: tenantUuid },
      },
    } = this.props;

    this.setState({ currentPage: page, selectedItemId: 0 });

    loadPackages({
      tenantUuid,
      page,
      isActive: true,
      limit,
      meta: {},
      search: tags,
    });
  };

  searchAsset = (tags: Array<string>, page = 1, limit = 9) => {
    const { loadAssets } = this.props;

    this.setState({ currentPage: page, selectedItemId: 0 });
    loadAssets({ page, limit, isActive: true, search: tags });
  };

  handleGrant = (closeModal: () => void) => () => {
    const { endDate, time, selectedItemId } = this.state;
    const { grantAccess, customer } = this.props;
    const { id } = customer || { id: -1 };
    const dateTime = `${endDate.format(dateFormat)} ${time.format(hourFormat)}`;
    grantAccess(id, dateTime, selectedItemId);
    closeModal();
  };

  handleTimeChange = (time: Moment) => {
    this.setState({ time });
  };

  handleCheck = (e: ChangeEvent<HTMLInputElement>) => {
    const { resetPackageAssets } = this.props;

    this.setState({
      value: e.target.value,
      tags: [],
      selectedItemId: 0,
    });
    resetPackageAssets();
  };

  handleDateChange = (date: Moment) => {
    this.setState({ endDate: date, activePeriod: null });
  };

  handleRangeClick = (period: Period) => () => {
    this.setState({ activePeriod: period });
    this.onPeriodChange(period);
  };

  onPeriodChange = (period: Period) => {
    if (!Operations[period]) {
      return;
    }
    const { duration, type } = Operations[period];
    const endDate = moment().add(duration, type);
    this.setState({ endDate });
  };

  makeAssetItemsCollection = (items: Array<AssetItem>) => {
    const collection = items.map((item: AssetItem) => {
      const { id, title: label, metadata: { paywall_cover_photo: thumbnail = '' } = {} } = item;
      return { id, label, thumbnail: thumbnail || defaultAssetItemImg };
    });

    return collection;
  };

  makePackageItemsCollection = (items: Array<PackageItem>) => {
    const collection = items.map((item: PackageItem) => {
      const { id, title: label, metadata: { thumbnail_image: thumbnail = '' } = {} } = item;
      return { id, label, thumbnail: thumbnail || packagePreviewImg };
    });

    return collection;
  };

  render() {
    const { value, tags, selectedItemId, endDate, time, activePeriod, currentPage } = this.state;
    const { customerEmail, assets, packages } = this.props;
    const { collection, isFetching, total: totalItemsCount } = value === ItemType.Asset ? assets : packages;

    const showError = value && tags.length > 0 && !isFetching;
    const showResult = !!collection.length;

    const numColumns = 3;
    const limitPerPage = 9;

    const periodContent = periods.map((period) => (
      <GrantAccessPeriod
        key={period}
        label={period}
        active={activePeriod === period}
        onClick={this.handleRangeClick(period)}
      />
    ));

    const items =
      value === ItemType.Asset
        ? this.makeAssetItemsCollection(collection)
        : this.makePackageItemsCollection(collection);

    return (
      <BaseModal
        tag="grant_access_modal"
        isOverflowVisible
        modalName={ModalNames.GrantAccess}
        title="Grant access"
        render={({ closeModal }: ModalActions) => (
          <>
            <StyledContainer columns={4} margin="1rem 0 0 0">
              <Cell middle width={1}>
                <Typography variant="p">User email:</Typography>
              </Cell>
              <Cell middle width={3}>
                <Typography variant="p">{customerEmail}</Typography>
              </Cell>
            </StyledContainer>
            <RadioButtonsContainer columns={4} margin="1rem 0 0 0">
              <Cell middle width={1}>
                <Typography variant="p">Search by: </Typography>
              </Cell>
              <Cell width={2}>
                <Radio
                  tag="radiobutton_asset_name_or_id"
                  id="asset"
                  value="asset"
                  name="search"
                  label="Asset ID/Name"
                  onChange={this.handleCheck}
                  checked={value === ItemType.Asset}
                />
                <Radio
                  tag="radiobutton_package_name_or_id"
                  id="package"
                  value="package"
                  name="search"
                  label="Package ID/Name"
                  onChange={this.handleCheck}
                  checked={value === ItemType.Package}
                />
              </Cell>
            </RadioButtonsContainer>
            <VerticalContainer>
              {value !== null && (
                <>
                  <SearchTooltip placement="top" />
                  <DivWithMargin>
                    <Filter options={suggestions} onSearch={this.handleAddition} />
                  </DivWithMargin>
                </>
              )}
            </VerticalContainer>
            {showResult ? (
              <>
                <ThumbnailItemPicker
                  items={items}
                  totalItemsCount={totalItemsCount}
                  numColumns={numColumns}
                  limitPerPage={limitPerPage}
                  onPageSelect={this.handlePageSelect}
                  currentPage={currentPage}
                  isLoading={isFetching}
                  onItemSelect={this.onItemSelect}
                />
                <AccessPeriodContainer columns={12} margin="1rem 0 0 0">
                  {selectedItemId ? (
                    <>
                      <Cell middle width={3}>
                        <Typography variant="p">Choose an interval:</Typography>
                      </Cell>
                      <Cell middle width={4}>
                        <PeriodsWrapper>{periodContent}</PeriodsWrapper>
                      </Cell>
                      <Cell middle width={2}>
                        <SingleDayPicker handleDateChange={this.handleDateChange} date={endDate} />
                      </Cell>
                      <Cell middle width={2}>
                        <TimeHourPicker handleTimeChange={this.handleTimeChange} time={time} />
                      </Cell>
                      <UtcWrapper middle width={1}>
                        <Label>UTC</Label>
                        <Tooltip placement="top" content="Please enter UTC time">
                          <MdInfoOutline />
                        </Tooltip>
                      </UtcWrapper>
                    </>
                  ) : null}
                </AccessPeriodContainer>
                <ButtonContainer columns={3} alignContent="center" justifyContent="center">
                  <Cell middle left={3}>
                    <Button
                      tag="button_grant"
                      fullHeight
                      type="button"
                      onClick={this.handleGrant(closeModal)}
                      buttonModifiers={['buttonPrimary']}
                    >
                      Grant
                    </Button>
                  </Cell>
                </ButtonContainer>
              </>
            ) : (
              <ErrorMessage>{showError && 'Please insert a valid search value.'}</ErrorMessage>
            )}
          </>
        )}
      />
    );
  }
}
